import React, { FC } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { ReactComponent as Movement_insightsIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/movement_insights.svg';
import { ReactComponent as UpIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/programs/up.svg';
import { ReactComponent as DownIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/programs/down.svg';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          background: '#0b0c25',
          padding: '8px 12px',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px',
          color: '#fff',
        }}
      >
        <p
          className="label"
          style={{ margin: '0 0 4px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}
        >
          {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p
            key={index}
            style={{ color: entry.color, margin: 0, fontSize: '14px', fontWeight: 600 }}
          >
            {entry.name}: {entry.value}%
          </p>
        ))}
      </div>
    );
  }
  return null;
};

interface MovementProgressionChartProps {
  baseValues: { mobility: number; stability: number; balance: number; quality: number }[];
  legendData: {
    key: string;
    label: string;
    color: string;
    status: string;
    trend: string;
    percentage: string;
  }[];
}
export const MovementProgressionChart: FC<MovementProgressionChartProps> = ({
  baseValues,
  legendData,
}) => {
  const generateDynamicData = () => {
    const generatedData = [];
    const today = new Date();

    const formatMonthDay = (date: Date) => {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const day = date.getDate().toString().padStart(2, '0');
      return `${months[date.getMonth()]} ${day}`;
    };

    // Map each data point to formatted historical dates spaced ~monthly
    const count = baseValues.length;
    for (let i = 0; i < count; i++) {
      const daysAgo = (count - 1 - i) * 28;
      const d = new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000);
      generatedData.push({
        name: formatMonthDay(d),
        ...baseValues[i],
      });
    }

    return generatedData;
  };

  const data = generateDynamicData();
  return (
    <div className="program-card movement-progression-card">
      <div className="card-header">
        <div className="icon">
          <Movement_insightsIcon />
        </div>
        <h3>Movement Progression</h3>
      </div>
      <div className="card-body">
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 15, right: 12, left: -25, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255, 255, 255, 0.05)" vertical={true} horizontal={true} />
              <XAxis
                dataKey="name"
                axisLine={{ stroke: 'rgba(255, 255, 255, 0.05)' }}
                tickLine={false}
                // @ts-expect-error lineHeight
                tick={{ fill: '#FFFFFFB2', fontSize: 11, lineHeight: 14 }}
                dy={8}
              />
              <YAxis
                axisLine={{ stroke: 'rgba(255, 255, 255, 0.05)' }}
                tickLine={false}
                // @ts-expect-error lineHeight
                tick={{ fill: '#FFFFFFB2', fontSize: 11, lineHeight: 14 }}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
              />

              {/* Mobility Line */}
              <Line
                type="monotone"
                dataKey="mobility"
                name="Mobility"
                stroke="#755BDF"
                strokeWidth={1.5}
                dot={{ r: 2.5, fill: '#755BDF', strokeWidth: 0 }}
                activeDot={{ r: 4.5 }}
                connectNulls={false}
              />
              {/* Stability Line */}
              <Line
                type="monotone"
                dataKey="stability"
                name="Stability"
                stroke="#42CDFF"
                strokeWidth={1.5}
                dot={{ r: 2.5, fill: '#42CDFF', strokeWidth: 0 }}
                activeDot={{ r: 4.5 }}
                connectNulls={false}
              />
              {/* Balance Line */}
              <Line
                type="monotone"
                dataKey="balance"
                name="Balance"
                stroke="#22D543"
                strokeWidth={1.5}
                dot={{ r: 2.5, fill: '#22D543', strokeWidth: 0 }}
                activeDot={{ r: 4.5 }}
                connectNulls={false}
              />
              {/* Movement Quality Line */}
              <Line
                type="monotone"
                dataKey="quality"
                name="Quality"
                stroke="#FFA100"
                strokeWidth={1.5}
                dot={{ r: 2.5, fill: '#FFA100', strokeWidth: 0 }}
                activeDot={{ r: 4.5 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="custom-legend">
          {legendData.map((item) => (
            <div className="legend-item" key={item.key}>
              <div className="legend-title">
                <span className="dot" style={{ backgroundColor: item.color }}></span>
                <span className="label">{item.label}:</span>
              </div>
              <div className="legend-status">
                <span className="status-text">{item.status}</span>
                <div className={`trend-pill ${item.trend}`}>
                  {item.trend === 'up' ? <UpIcon /> : <DownIcon />}
                  <span>{item.percentage}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
